import { ActionIcon, Alert, Box, Button, Group, Loader, NumberInput, SegmentedControl, Select, Stack, Text, Title, Tooltip } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconChevronLeft, IconChevronRight, IconCopy, IconLock } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { api, PlanMetric, SOURCE_NAMES } from '../api';
import { dayjs, monthName, monthStart } from '../format';
import { initialLe, rememberLe } from '../lastLe';
import { defaults, hasRole, useSession } from '../session';

type Values = Record<string, number | ''>;
const num = (v: string | null) => (v === null ? '' : Number(v));
const fmt = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 });
const unitText = (u: PlanMetric['unit']) => (u === 'rub' ? ' ₽' : u === 'percent' ? ' %' : '');

/** Планы по показателям на месяц: по ООО, по подразделениям. Повторное сохранение заменяет план. */
export function PlansPage() {
  const { me, catalog } = useSession();
  const headOnly = !hasRole(me, 'owner', 'finance', 'manager');
  const [month, setMonth] = useState(monthStart(new Date()));
  const [legalEntity, setLegalEntity] = useState<string | null>(() => initialLe(me, catalog));
  const [department, setDepartment] = useState<string | null>(headOnly ? defaults(me).departmentId : null);
  const [data, setData] = useState<{ can_edit: boolean; metrics: PlanMetric[] } | null>(null);
  const [values, setValues] = useState<Values>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!legalEntity) return setData(null);
    setData(null);
    // все доступные подразделения сразу: фильтр по подразделению — на экране, правки не теряются
    const q = new URLSearchParams({ month, legal_entity_id: legalEntity });
    api
      .get<{ can_edit: boolean; metrics: PlanMetric[] }>(`/plans?${q}`)
      .then((r) => {
        setData(r);
        setValues(Object.fromEntries(r.metrics.map((m) => [m.id, num(m.value)])));
      })
      .catch((e) => notifications.show({ color: 'red', message: e.message }));
  }, [month, legalEntity]);

  const groups = useMemo(() => {
    const g = new Map<string, PlanMetric[]>();
    for (const m of (data?.metrics ?? []).filter((x) => !department || x.department_id === department)) {
      const k = `${m.department} · ${m.group_name}`;
      g.set(k, [...(g.get(k) ?? []), m]);
    }
    return [...g];
  }, [data, department]);

  if (!catalog) return null;
  const metrics = data?.metrics ?? [];
  const changed = metrics.filter((m) => m.editable && num(m.value) !== values[m.id]);
  const copyable = metrics.filter((m) => m.editable && values[m.id] === '' && m.prev_value !== null && (!department || m.department_id === department));
  // смена месяца или ООО перечитывает план: несохранённые правки — только после подтверждения
  const guard = (go: () => void) =>
    changed.length
      ? modals.openConfirmModal({
          title: 'Не сохранено',
          children: <Text size="sm">{`Изменений: ${changed.length}. Если перейти, они пропадут.`}</Text>,
          labels: { confirm: 'Перейти без сохранения', cancel: 'Остаться' },
          onConfirm: go,
        })
      : go();
  const departments = headOnly ? catalog.departments.filter((d) => me?.roles.some((r) => r.department_id === d.id)) : catalog.departments;

  async function save() {
    if (!legalEntity) return;
    setBusy(true);
    try {
      const r = await api.put<{ saved: number; removed: number; skipped: number }>('/plans', {
        month,
        legal_entity_id: legalEntity,
        values: changed.map((m) => ({ metric_id: m.id, value: values[m.id] === '' ? null : values[m.id] })),
      });
      setData((d) => d && { ...d, metrics: d.metrics.map((m) => (changed.includes(m) ? { ...m, value: values[m.id] === '' ? null : String(values[m.id]), source: 'manual' } : m)) });
      notifications.show({ color: 'brand', message: `План сохранён${r.skipped ? `; из таблицы, не изменено: ${r.skipped}` : ''}` });
    } catch (e) {
      notifications.show({ color: 'red', message: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box maw={760} mx="auto" style={{ minHeight: 'calc(100vh - 56px)', display: 'flex', flexDirection: 'column' }}>
      <Stack gap="sm" p="md" pb={0}>
        <Title order={2}>Планы</Title>
        <Group gap="sm" wrap="wrap">
          <Group gap={4} className="mis-card" px={6} py={4}>
            <ActionIcon variant="subtle" aria-label="Предыдущий месяц" onClick={() => guard(() => setMonth(monthStart(dayjs(month).subtract(1, 'month').toDate())))}>
              <IconChevronLeft size={18} />
            </ActionIcon>
            <Text fw={500} w={140} ta="center">
              {monthName(month)}
            </Text>
            <ActionIcon variant="subtle" aria-label="Следующий месяц" onClick={() => guard(() => setMonth(monthStart(dayjs(month).add(1, 'month').toDate())))}>
              <IconChevronRight size={18} />
            </ActionIcon>
          </Group>
          <Select
            aria-label="Подразделение"
            placeholder="Все подразделения"
            clearable={!headOnly}
            data={departments.map((d) => ({ value: d.id, label: d.name }))}
            value={department}
            onChange={setDepartment}
            w={{ base: '100%', sm: 240 }}
          />
        </Group>
        <SegmentedControl
          fullWidth
          value={legalEntity ?? ''}
          onChange={(v) =>
            guard(() => {
              setLegalEntity(v);
              rememberLe(v);
            })
          }
          data={catalog.legalEntities.map((l) => ({ value: l.id, label: l.name }))}
        />
      </Stack>

      <Stack gap="md" p="md" style={{ flex: 1 }}>
        {!legalEntity && (
          <Alert color="brand" variant="light">
            Выберите ООО, для которого смотрите или вносите план.
          </Alert>
        )}
        {legalEntity && !data && (
          <Group justify="center" p="xl">
            <Loader />
          </Group>
        )}
        {data && !data.can_edit && (
          <Alert color="gray" variant="light">
            План вносят собственник, финансист и управляющая этого ООО. Здесь он только для просмотра.
          </Alert>
        )}
        {data && metrics.length === 0 && <Text c="dimmed">Нет показателей</Text>}
        {data?.can_edit && copyable.length > 0 && (
          <Button
            variant="light"
            leftSection={<IconCopy size={16} />}
            onClick={() => setValues((v) => ({ ...v, ...Object.fromEntries(copyable.map((m) => [m.id, num(m.prev_value)])) }))}
            style={{ alignSelf: 'flex-start' }}
          >
            Заполнить пустые как в прошлом месяце ({copyable.length})
          </Button>
        )}
        {groups.map(([title, items]) => (
          <Box key={title} className="mis-card" px="md" py={6}>
            <Text size="xs" fw={600} c="dimmed" tt="uppercase" mt={8} mb={2} style={{ letterSpacing: 0.6 }}>
              {title}
            </Text>
            {items.map((m, i) => (
              <Group key={m.id} justify="space-between" wrap="nowrap" mih={60} style={{ borderTop: i ? '1px solid #F0EEE9' : 0 }}>
                <div style={{ minWidth: 0 }}>
                  <Group gap={6} wrap="nowrap">
                    {!m.editable && (
                      <Tooltip label={m.source && m.source !== 'manual' ? `${SOURCE_NAMES[m.source]}: правится там` : 'Только просмотр'}>
                        <IconLock size={14} color="var(--mis-muted)" aria-label="Только просмотр" />
                      </Tooltip>
                    )}
                    <Text size="md">{m.name}</Text>
                  </Group>
                  {m.prev_value !== null && (
                    <Text size="xs" c="dimmed">
                      в прошлом месяце: {fmt.format(Number(m.prev_value))}
                      {unitText(m.unit)}
                    </Text>
                  )}
                </div>
                <NumberInput
                  aria-label={`${m.group_name} — ${m.name}`}
                  w={160}
                  min={0}
                  decimalScale={m.unit === 'count' ? 0 : 2}
                  thousandSeparator=" "
                  decimalSeparator=","
                  hideControls
                  disabled={!m.editable}
                  rightSection={m.unit === 'count' ? undefined : <Text c="dimmed">{m.unit === 'rub' ? '₽' : '%'}</Text>}
                  value={values[m.id] ?? ''}
                  onChange={(v) => setValues((x) => ({ ...x, [m.id]: v === '' ? '' : Number(v) }))}
                  styles={{ input: { textAlign: 'right', fontWeight: 600 } }}
                  inputMode="decimal"
                />
              </Group>
            ))}
          </Box>
        ))}
      </Stack>

      {data?.can_edit && (
        <Stack gap={8} p="md" pb="lg" className="mis-sticky-footer">
          <Text size="sm" c="dimmed" ta="center">
            Если сохранить ещё раз, план месяца заменится. Пустое поле — плана нет.
          </Text>
          <Button size="lg" onClick={save} loading={busy} disabled={!changed.length}>
            {changed.length ? `Сохранить план (${changed.length})` : 'Сохранить план'}
          </Button>
        </Stack>
      )}
    </Box>
  );
}
